import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

// Helper function to get localized category name
const getLocalizedCategoryName = (fullName, language) => {
  if (!fullName) return "";
  const translations = fullName.split("|");
  switch (language) {
    case "en":
      return translations[0] || "";
    case "fr":
      return translations[1] || translations[0] || "";
    case "ar":
      return translations[2] || translations[0] || "";
    default:
      return translations[0] || "";
  }
};
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CardActions,
  Button,
  TextField,
  MenuItem,
  Box,
  LinearProgress,
  Pagination,
  IconButton,
  Menu,
  Chip,
  Rating,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  useTheme,
  alpha,
} from "@mui/material";
import LazyImage from "../../components/common/LazyImage";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  Clear as ClearIcon,
  Category as CategoryIcon,
  Star as StarIcon,
  Lock as LockIcon,
  CalendarToday as CalendarTodayIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { booksAPI, categoriesAPI } from "../../services/api";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import CustomContainer from "../../components/common/CustomContainer";

function BookCard({ book, viewMode, onRatingChange }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const progress = book.reading_progress || 0;
  const theme = useTheme();
  const [elevation, setElevation] = useState(1);

  const apiBaseUrl =
    import.meta.env.VITE_API_URL || "https://ebibliospace.com/api";

  const coverImageUrl = book.cover_image_path
    ? book.cover_image_path.startsWith("http")
      ? book.cover_image_path
      : book.cover_image_path.startsWith("/")
      ? `${apiBaseUrl}${book.cover_image_path}`
      : `${apiBaseUrl}/${book.cover_image_path}`
    : "/placeholder-cover.jpg";

  // Format publication date if available
  const publicationDate = book.publication_date 
    ? new Date(book.publication_date).getFullYear() 
    : null;

  // Get category name if available
  const categoryName = book.category_name 
    ? getLocalizedCategoryName(book.category_name, i18n.language)
    : book.Category?.name 
      ? getLocalizedCategoryName(book.Category.name, i18n.language)
      : "";

  if (viewMode === "list") {
    return (
      <Card 
        elevation={elevation}
        onMouseOver={() => setElevation(3)}
        onMouseOut={() => setElevation(1)}
        sx={{ 
          display: "flex", 
          mb: 2, 
          width: "100%",
          borderRadius: 2,
          overflow: "hidden",
          transition: "box-shadow 0.3s ease-in-out",
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        }}
      >
        <Box
          sx={{
            width: 140,
            height: 200,
            flexShrink: 0,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <LazyImage 
            src={coverImageUrl} 
            alt={book.title} 
            sx={{
              transition: "transform 0.3s ease-in-out",
              "&:hover": {
                transform: "scale(1.05)"
              }
            }}
          />
          {progress > 0 && (
            <Box 
              sx={{ 
                position: "absolute", 
                bottom: 0, 
                left: 0, 
                right: 0,
                bgcolor: alpha(theme.palette.primary.main, 0.8),
                py: 0.5,
                px: 1
              }}
            >
              <Typography variant="caption" sx={{ color: "white", fontWeight: 500 }}>
                {progress}% {t("books.progress.completed")}
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", p: 2, flex: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              {book.title}
            </Typography>
            <Rating
              value={book.average_rating || 0}
              readOnly
              size="small"
              precision={0.5}
              sx={{ ml: 1 }}
            />
          </Box>
          
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            {book.author}
          </Typography>
          
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
            {categoryName && (
              <Chip 
                icon={<CategoryIcon fontSize="small" />} 
                label={categoryName} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            )}
            {publicationDate && (
              <Chip 
                icon={<CalendarTodayIcon fontSize="small" />} 
                label={publicationDate} 
                size="small" 
                variant="outlined"
              />
            )}
          </Box>
          
          {book.description && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mt: 2, 
                display: "-webkit-box", 
                WebkitLineClamp: 2, 
                WebkitBoxOrient: "vertical", 
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {book.description}
            </Typography>
          )}
          
          {progress > 0 && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ 
                  height: 6, 
                  borderRadius: 5,
                  bgcolor: alpha(theme.palette.primary.main, 0.1)
                }}
              />
            </Box>
          )}
          
          <Box sx={{ mt: "auto", pt: 2, display: "flex", justifyContent: "space-between" }}>
            <Button 
              variant="contained" 
              size="small" 
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate(`/books/${book.id}`)}
              sx={{ 
                borderRadius: 2,
                boxShadow: 1
              }}
            >
              {progress > 0
                ? t("books.actions.continue")
                : t("books.actions.start")}
            </Button>
            
            {book.has_access === false && (
              <Tooltip title={t("books.access.requestNeeded")}>
                <Chip 
                  icon={<LockIcon fontSize="small" />} 
                  label={t("books.access.restricted")} 
                  size="small" 
                  color="warning"
                />
              </Tooltip>
            )}
          </Box>
        </Box>
      </Card>
    );
  }

  return (
    <Card 
      elevation={elevation}
      onMouseOver={() => setElevation(3)}
      onMouseOut={() => setElevation(1)}
      sx={{ 
        height: "100%", 
        display: "flex", 
        flexDirection: "column",
        borderRadius: 2,
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        transform: elevation === 3 ? "translateY(-8px)" : "none",
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      }}
    >
      <Box
        sx={{
          position: "relative",
          paddingTop: "140%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            transition: "transform 0.3s ease-in-out",
            "&:hover": {
              transform: "scale(1.05)"
            }
          }}
        >
          <LazyImage src={coverImageUrl} alt={book.title} />
        </Box>
        
        {categoryName && (
          <Chip 
            label={categoryName} 
            size="small" 
            color="primary" 
            sx={{ 
              position: "absolute", 
              top: 8, 
              right: 8,
              bgcolor: alpha(theme.palette.primary.main, 0.85),
              color: "white",
              fontWeight: 600,
              px: 1,
              backdropFilter: "blur(4px)"
            }} 
          />
        )}
        
        {progress > 0 && (
          <Box 
            sx={{ 
              position: "absolute", 
              bottom: 0, 
              left: 0, 
              right: 0,
              bgcolor: alpha(theme.palette.primary.main, 0.8),
              py: 0.5,
              px: 1
            }}
          >
            <Typography variant="caption" sx={{ color: "white", fontWeight: 500 }}>
              {progress}% {t("books.progress.completed")}
            </Typography>
          </Box>
        )}
      </Box>
      
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Typography 
            gutterBottom 
            variant="h6" 
            component="div" 
            noWrap 
            sx={{ 
              fontWeight: 600,
              fontSize: "1.1rem",
              width: "100%",
              textOverflow: "ellipsis",
              "&:hover": { color: theme.palette.primary.main }
            }}
          >
            {book.title}
          </Typography>
          
          {book.has_access === false && (
            <Tooltip title={t("books.access.requestNeeded")}>
              <LockIcon fontSize="small" color="warning" sx={{ ml: 0.5, flexShrink: 0 }} />
            </Tooltip>
          )}
        </Box>
        
        <Typography 
          variant="body2" 
          color="text.secondary" 
          noWrap 
          sx={{ mb: 1, fontStyle: "italic" }}
        >
          {book.author}
        </Typography>
        
        <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
          <Rating
            value={Number(book.average_rating) || 0}
            readOnly
            size="small"
            precision={0.5}
          />
          {Number(book.average_rating) > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              ({Number(book.average_rating).toFixed(1)})
            </Typography>
          )}
        </Box>
        
        {publicationDate && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            <CalendarTodayIcon fontSize="inherit" sx={{ fontSize: "0.75rem", mr: 0.5, verticalAlign: "text-top" }} />
            {publicationDate}
          </Typography>
        )}
      </CardContent>
      
      <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
        <Button 
          variant="contained" 
          size="small" 
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate(`/books/${book.id}`)}
          sx={{ 
            borderRadius: 2,
            boxShadow: 1,
            textTransform: "none",
            px: 2
          }}
          fullWidth
        >
          {progress > 0
            ? t("books.actions.continue")
            : t("books.actions.start")}
        </Button>
      </CardActions>
    </Card>
  );
}

function BookList() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const [searchInput, setSearchInput] = useState("");
  const { showSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState(null);

  const [filters, setFilters] = useState({
    search: "",
    category: searchParams.get("category") || "all",
    sort: "title",
    minRating: 0,
  });

  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
  });

  const fetchBooks = useCallback(async () => {
    try {
      setLoading(true);

      const queryParams = {
        page: pagination.page,
        limit: 9,
        sort: filters.sort,
        category: filters.category !== "all" ? filters.category : undefined,
        search: filters.search || undefined,
        minRating: filters.minRating > 0 ? filters.minRating : undefined,
      };

      const [booksResponse, categoriesResponse] = await Promise.all([
        booksAPI.getAll(queryParams),
        categoriesAPI.getAll(),
      ]);

      if (booksResponse.data && categoriesResponse.data) {
        setBooks(booksResponse.data.books || []);
        setCategories(categoriesResponse.data.categories || []);
        setPagination((prev) => ({
          ...prev,
          totalPages: booksResponse.data.totalPages || 1,
        }));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar(
        error.response?.data?.message || "Failed to load data",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, showSnackbar]);

  // Search is now triggered by button click instead of automatically with debounce

  useEffect(() => {
    const category = searchParams.get("category") || "all";
    setFilters((prev) => ({ ...prev, category }));
  }, [searchParams]);

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  const handleFilterChange = (name, value) => {
    if (name === "search") {
      setSearchInput(value);
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
      setPagination((prev) => ({ ...prev, page: 1 }));

      if (name === "category") {
        if (value === "all") {
          searchParams.delete("category");
        } else {
          searchParams.set("category", value);
        }
        setSearchParams(searchParams);
      }
    }
  };
  
  const handleSearch = () => {
    setFilters((prev) => ({ 
      ...prev, 
      search: searchInput
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };
  
  const handleResetSearch = () => {
    setSearchInput("");
    setFilters({
      search: "",
      category: "all",
      sort: "title",
      minRating: 0,
    });
    setPagination({ page: 1, totalPages: 1 });
    
    // Reset URL parameters
    searchParams.delete("category");
    setSearchParams(searchParams);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  if (loading) {
    return (
      <Box sx={{ width: "100%", mt: 4, textAlign: "center" }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <CustomContainer>
      {/* Professional Search Bar */}
      <Card sx={{ mb: 4, p: 2, boxShadow: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Search input field */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("books.search.placeholder")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              }}
              variant="outlined"
            />
          </Grid>
          
          {/* Category dropdown */}
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              value={filters.category}
              onChange={(e) => handleFilterChange("category", e.target.value)}
              label={t("books.filters.category")}
              InputProps={{
                startAdornment: <CategoryIcon color="action" fontSize="small" sx={{ mr: 1 }} />,
              }}
            >
              <MenuItem value="all">
                {t("books.filters.allCategories")}
              </MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {getLocalizedCategoryName(category.name, i18n.language)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          {/* Rating dropdown */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("books.filters.minRating")}</InputLabel>
              <Select
                value={filters.minRating}
                label={t("books.filters.minRating")}
                onChange={(e) =>
                  handleFilterChange("minRating", e.target.value)
                }
                startAdornment={<StarIcon color="action" fontSize="small" sx={{ ml: 1, mr: 1 }} />}
              >
                <MenuItem value={0}>{t("books.filters.anyRating")}</MenuItem>
                <MenuItem value={4}>{t("books.filters.fourPlus")}</MenuItem>
                <MenuItem value={3}>{t("books.filters.threePlus")}</MenuItem>
                <MenuItem value={2}>{t("books.filters.twoPlus")}</MenuItem>
                <MenuItem value={1}>{t("books.filters.onePlus")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Action buttons */}
          <Grid item xs={12} md={2}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Tooltip title={t("books.search.button")}>
                <IconButton
                  onClick={handleSearch}
                  color="primary"
                  size="medium"
                  sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
                >
                  <SearchIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title={t("books.search.reset")}>
                <IconButton
                  onClick={handleResetSearch}
                  color="secondary"
                  size="medium"
                >
                  <ClearIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title={viewMode === "grid" ? t("books.view.list") : t("books.view.grid")}>
                <IconButton
                  onClick={() => setViewMode((prev) => (prev === "grid" ? "list" : "grid"))}
                  color="inherit"
                  size="medium"
                >
                  {viewMode === "grid" ? <ListViewIcon /> : <GridViewIcon />}
                </IconButton>
              </Tooltip>
              
              <Tooltip title={t("books.sort.options")}>
                <IconButton
                  onClick={handleMenuOpen}
                  color="inherit"
                  size="medium"
                >
                  <FilterIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* Sort Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            handleFilterChange("sort", "title");
            handleMenuClose();
          }}
        >
          {t("books.sort.byTitle")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleFilterChange("sort", "author");
            handleMenuClose();
          }}
        >
          {t("books.sort.byAuthor")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleFilterChange("sort", "rating");
            handleMenuClose();
          }}
        >
          {t("books.sort.byRating")}
        </MenuItem>
      </Menu>

      {/* Books Grid/List */}
      {books.length > 0 ? (
        <>
          <Grid container spacing={3}>
            {books.map((book) => (
              <Grid
                item
                key={book.id}
                xs={12}
                sm={viewMode === "grid" ? 6 : 12}
                md={viewMode === "grid" ? 4 : 12}
              >
                <BookCard
                  book={book}
                  viewMode={viewMode}
                  onRatingChange={fetchBooks}
                />
              </Grid>
            ))}
          </Grid>
          {pagination.totalPages > 1 && (
            <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={(_, value) =>
                  setPagination((prev) => ({ ...prev, page: value }))
                }
                color="primary"
              />
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            {t("books.messages.noBooks")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("books.messages.adjustFilters")}
          </Typography>
        </Box>
      )}
    </CustomContainer>
  );
}

export default BookList;
